import { createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';

// Staff roles: super_admin = platform owner, owner = org owner, others = team members
export type StaffRole = 'super_admin' | 'owner' | 'supervisor' | 'cleaner' | null;

export interface OrganizationMembership {
  organizationId: string;
  organizationName: string;
  role: 'owner' | 'member';
}

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  isPlatformAdmin: boolean;
  isUatReviewer: boolean;
  isSuperAdmin: boolean; // Platform admin (founders) - from user_roles
  staffRole: StaffRole;
  currentOrgRole: 'owner' | 'member' | null;
  isOrgOwner: boolean; // Organization owner - from organization_members
  organizationMembership: OrganizationMembership | null;
  organizationMemberships: OrganizationMembership[];
  loading: boolean;
  rolesLoading: boolean; // Separate loading state for roles/org membership
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  refreshOrgMembership: () => Promise<void>;
  switchOrganization: (organizationId: string) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
